/**
 * Route Audit System for DMV Soundstage
 * 
 * This module provides utilities to audit all routes and API endpoints
 * for broken links, missing handlers, and permission issues.
 */

import { API } from '../config/api';

// All frontend routes from App.js
export const ALL_ROUTES = [
  // Public routes
  { path: '/', name: 'Landing Page', auth: false },
  { path: '/auth', name: 'Login/Signup', auth: false },
  { path: '/forgot-password', name: 'Forgot Password', auth: false },
  { path: '/reset-password', name: 'Reset Password', auth: false },
  { path: '/terms', name: 'Terms', auth: false },
  { path: '/privacy', name: 'Privacy', auth: false },
  { path: '/community-guidelines', name: 'Community Guidelines', auth: false },
  { path: '/dmca', name: 'DMCA', auth: false },
  { path: '/contact', name: 'Contact', auth: false },
  { path: '/pricing', name: 'Pricing', auth: false },
  { path: '/fees', name: 'Fees', auth: false },
  { path: '/why', name: 'About/Why', auth: false },
  { path: '/about', name: 'About', auth: false },
  
  // Auth required - General
  { path: '/home', name: 'Home', auth: true },
  { path: '/feed', name: 'Feed', auth: false },
  { path: '/reels', name: 'Soundstage Clips', auth: false },
  { path: '/reels/create', name: 'Create Clip', auth: true },
  { path: '/stories/create', name: 'Create Story', auth: true },
  { path: '/post/create', name: 'Create Post', auth: true },
  { path: '/search', name: 'Search', auth: false },
  { path: '/notifications', name: 'Notifications', auth: true },
  { path: '/settings', name: 'Settings', auth: true },
  
  // Content pages
  { path: '/artists', name: 'Artists', auth: false },
  { path: '/tracks', name: 'Tracks', auth: false },
  { path: '/videos', name: 'Videos', auth: false },
  { path: '/charts', name: 'Charts', auth: false },
  { path: '/events', name: 'Events', auth: false },
  { path: '/battles', name: 'Battles', auth: false },
  { path: '/studios', name: 'Studios', auth: false },
  { path: '/interviews', name: 'Interviews', auth: false },
  { path: '/creatives', name: 'Creatives', auth: false },
  { path: '/marketplace', name: 'Marketplace', auth: false },
  { path: '/live', name: 'Live', auth: false },
  { path: '/analytics', name: 'Analytics', auth: true },
  { path: '/leaderboard', name: 'Leaderboard', auth: false },
  { path: '/playlists', name: 'Playlists', auth: false },
  { path: '/perks', name: 'Perks', auth: true },
  { path: '/listener-perks', name: 'Listener Perks', auth: true },
  { path: '/my-subscriptions', name: 'My Subscriptions', auth: true },
  { path: '/social-sync', name: 'Social Sync', auth: true },
  { path: '/spotlight', name: 'Community Spotlight', auth: false },
  { path: '/community', name: 'Community', auth: false },
  
  // Dashboards
  { path: '/dashboard', name: 'Dashboard', auth: true },
  { path: '/dashboard/artist', name: 'Artist Dashboard', auth: true, role: 'artist' },
  { path: '/dashboard/listener', name: 'Listener Dashboard', auth: true, role: 'listener' },
  { path: '/dashboard/podcaster', name: 'Podcaster Dashboard', auth: true, role: 'podcaster' },
  { path: '/dashboard/streamer', name: 'Streamer Dashboard', auth: true, role: 'streamer' },
  { path: '/dashboard/studio', name: 'Studio Dashboard', auth: true, role: 'studio' },
  
  // Admin routes
  { path: '/admin/support-tickets', name: 'Admin Support', auth: true, role: 'admin' },
  { path: '/admin/social', name: 'Admin Social', auth: true, role: 'admin' },
  { path: '/admin/grant-tier', name: 'Admin Grant Tier', auth: true, role: 'admin' },
  { path: '/admin/featured', name: 'Admin Featured', auth: true, role: 'admin' },
  { path: '/admin/content-wipe', name: 'Admin Content Wipe', auth: true, role: 'admin' },
  { path: '/admin/spotlight', name: 'Admin Spotlight', auth: true, role: 'admin' },
  { path: '/admin/roles', name: 'Admin Roles', auth: true, role: 'admin' },
  { path: '/admin/errors', name: 'Admin Errors', auth: true, role: 'admin' },
  { path: '/admin/health', name: 'Admin Health', auth: true, role: 'admin' },
  { path: '/admin/data-hygiene', name: 'Admin Data Hygiene', auth: true, role: 'admin' },
  
  // Signup flows
  { path: '/signup/listener', name: 'Listener Signup', auth: false },
  { path: '/signup/artist', name: 'Artist Signup', auth: false },
  { path: '/signup/podcaster', name: 'Podcaster Signup', auth: false },
  { path: '/signup/streamer', name: 'Streamer Signup', auth: false },
  { path: '/signup/studio', name: 'Studio Signup', auth: false },
  { path: '/signup/advertiser', name: 'Advertiser Signup', auth: false },
];

// Critical API endpoints to test
export const CRITICAL_API_ENDPOINTS = [
  { method: 'GET', path: '/api/health', name: 'Health Check', auth: false },
  { method: 'GET', path: '/api/platform/config', name: 'Platform Config', auth: false },
  { method: 'GET', path: '/api/auth/me', name: 'Get Current User', auth: true },
  { method: 'GET', path: '/api/artists', name: 'List Artists', auth: false },
  { method: 'GET', path: '/api/tracks', name: 'List Tracks', auth: false },
  { method: 'GET', path: '/api/videos', name: 'List Videos', auth: false },
  { method: 'GET', path: '/api/reels', name: 'List Soundstage Clips', auth: false },
  { method: 'GET', path: '/api/events', name: 'List Events', auth: false },
  { method: 'GET', path: '/api/battles', name: 'List Battles', auth: false },
  { method: 'GET', path: '/api/interviews', name: 'List Interviews', auth: false },
  { method: 'GET', path: '/api/charts/top-20-artists', name: 'Top 20 Artists Chart', auth: false },
  { method: 'GET', path: '/api/me/notifications', name: 'My Notifications', auth: true },
  { method: 'GET', path: '/api/me/permissions', name: 'User Permissions', auth: true },
  { method: 'GET', path: '/api/feed/sponsored', name: 'Sponsored Feed', auth: false },
  { method: 'GET', path: '/api/creatives/directory', name: 'Creatives Directory', auth: false },
  { method: 'GET', path: '/api/live/streams', name: 'Live Streams', auth: false },
  { method: 'GET', path: '/api/community-spotlight', name: 'Community Spotlight', auth: false },
];

/**
 * Test a single API endpoint
 */
export async function testApiEndpoint(endpoint, token = null) {
  const result = {
    endpoint: endpoint.path,
    name: endpoint.name,
    method: endpoint.method,
    status: null,
    ok: false,
    error: null,
    responseTime: 0,
  };

  const startTime = Date.now();
  
  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token && endpoint.auth) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API}${endpoint.path.replace('/api', '')}`, {
      method: endpoint.method,
      headers,
      credentials: 'include',
    });

    result.status = response.status;
    result.responseTime = Date.now() - startTime;
    result.ok = response.ok || (response.status === 401 && !endpoint.auth);
    
    if (!response.ok) {
      try {
        const data = await response.json();
        result.error = data.detail || data.message || `HTTP ${response.status}`;
      } catch {
        result.error = `HTTP ${response.status}`;
      }
    }
  } catch (error) {
    result.responseTime = Date.now() - startTime;
    result.error = error.message || 'Network error';
  }

  return result;
}

/**
 * Test all critical API endpoints
 */
export async function auditApiEndpoints(token = null) {
  const results = {
    total: CRITICAL_API_ENDPOINTS.length,
    passed: 0,
    failed: 0,
    errors: [],
    details: [],
  };

  for (const endpoint of CRITICAL_API_ENDPOINTS) {
    const result = await testApiEndpoint(endpoint, token);
    results.details.push(result);
    
    if (result.ok) {
      results.passed++;
    } else {
      results.failed++;
      results.errors.push({
        endpoint: endpoint.path,
        name: endpoint.name,
        error: result.error,
        status: result.status,
      });
    }
  }

  return results;
}

/**
 * Test if a frontend route loads without errors
 */
export async function testFrontendRoute(route, navigate) {
  const result = {
    path: route.path,
    name: route.name,
    ok: false,
    error: null,
    loadTime: 0,
  };

  const startTime = Date.now();
  
  try {
    // This would need to be run in a browser context
    // For now, we'll just validate the route exists
    result.ok = true;
    result.loadTime = Date.now() - startTime;
  } catch (error) {
    result.loadTime = Date.now() - startTime;
    result.error = error.message;
  }

  return result;
}

/**
 * Log audit results to the server
 */
export async function logAuditResults(results, token) {
  try {
    await fetch(`${API}/client-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({
        type: 'ROUTE_AUDIT',
        message: `Route audit completed: ${results.passed}/${results.total} passed`,
        data: results,
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch (error) {
    console.error('[Route Audit] Failed to log results:', error);
  }
}

/**
 * Check if a button/link destination exists
 */
export function validateButtonDestination(href) {
  if (!href) return { valid: false, reason: 'No href provided' };
  
  // Check if it's an internal route
  if (href.startsWith('/')) {
    const route = ALL_ROUTES.find(r => r.path === href || href.startsWith(r.path + '/'));
    if (route) {
      return { valid: true, route };
    }
    // Check for dynamic routes
    if (href.match(/\/(artist|profile|org|tracks|videos|events|battles|interviews|reels|playlists)\//)) {
      return { valid: true, dynamic: true };
    }
    return { valid: false, reason: 'Route not found in registry' };
  }
  
  // External links are considered valid
  if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return { valid: true, external: true };
  }
  
  return { valid: false, reason: 'Invalid href format' };
}
